import {
  EmailFiltersParam,
  ExternalUsersFiltersParam,
  IGetPaginatedFiltersParams
} from './filtersSlice'
import { FILTERS_LIMIT_DEFAULT } from '../../constants'
import { FilterParams } from '../../interfaces'
import {
  calculateSkipAndLimit,
  getAfterCursor,
  parameterizeArrayofObjects
} from '../../utils/graphqlUtil'
import { NameIdSummary } from '../ropa/ropaSlice'
import { gql } from 'graphql-request'

export const queryDataSourceNames = (params: FilterParams): string => {
  const { ...listFilters } = params
  const filterString = listFilters.filter ? parameterizeArrayofObjects(listFilters.filter) : ''
  const paramString = filterString ? `(filter: ${filterString})` : ''

  return gql`
    {
      datasources${paramString} {
        edges {
          node {
            id
            name
            type
          }
        }
      }
    }
  `
}
export const mapQueryDataSourceNames = (raw: any): { list: NameIdSummary[] } => {
  const list = raw.datasources.edges.map(({ node: attr }) => ({
    name: attr.name,
    id: attr.id,
    type: attr.type
  }))

  return { list }
}

export interface IGetPoliciesByTypeParams {
  policyType: string
}
export const queryPoliciesIDNamesByType = (params: IGetPoliciesByTypeParams): string => gql`
{
  policy(
    first: 1,
    type: ${params.policyType}
  ) {
    edges {
      node {
        id
        name
      }
    }
  }
}
`

export const mapQueryPoliciesIDNamesByType = (
  raw: any
): { list: { id: string; name: string }[] } => {
  const list = raw.policy.edges?.map(({ node }) => ({ ...node }))
  return { list }
}

export const queryDrivesNames = (params: IGetPaginatedFiltersParams): string => {
  const { datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT } = params
  const cursor = getAfterCursor(page, pageSize)

  return gql`
  {
    drive(first: ${pageSize}, after: "${cursor}", datasourceId: "${datasourceId}"){
    count
      edges {
        node {
          driveId
          driveName
        }
      }
    }
  }
`
}

export const mapQueryDrivesNames = (
  raw: any
): { list: { id: string; driveName: string }[]; total: number } => {
  const list = raw.drive?.edges?.map(({ node }) => ({
    id: node.driveId,
    driveName: node.driveName
  }))
  return { list, total: raw.drive?.count }
}

export const queryProjectIdNames = (): string => {
  return gql`
    {
      projects {
        edges {
          node {
            id
            name
          }
        }
      }
    }
  `
}

export const mapQueryProjectIdNames = (raw: any): { list: { id: string; name: string }[] } => {
  try {
    return { list: raw.projects.edges.map(({ node: { name, id } }) => ({ name, id })) }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAwsBucketOwners = (params: IGetPaginatedFiltersParams): string => {
  const { datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT, filters } = params
  const cursor = getAfterCursor(page, pageSize)
  const filterString = filters?.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const paramString = filterString ? `, filter: ${filterString}` : ''
  const datasourceParam = datasourceId ? `, datasourceId: "${datasourceId}"` : ''

  return gql`
    {
       bucket(first: ${pageSize}, after: "${cursor}"${paramString}${datasourceParam}) {
        count 
        edges {
          node {
            ownerName
            ownerId
          }
        }
      }
    }
  `
}

export const mapQueryAwsBucketOwners = (
  raw: any
): { list: { id: string; name: string }[]; total: number } => {
  try {
    const list = raw.bucket.edges.map(({ node }) => ({ id: node.ownerId, name: node.ownerName }))

    return {
      list: [...new Map(list.map((item) => [item.id, item])).values()] as {
        id: string
        name: string
      }[],
      total: raw.bucket.count
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryRopaProcessOwners = (): string => {
  return gql`
    {
      ropaProcess(booleanFilter: [{ key: IS_SYSTEM_DEFINED, value: false }]) {
        edges {
          node {
            owner
          }
        }
      }
    }
  `
}

export const mapQueryRopaProcessOwners = (raw: any): string[] => {
  try {
    const list = (raw.ropaProcess?.edges?.map(({ node }) => node.owner || '') || []) as string[]
    return [...new Set(list)]
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAwsBucketFilters = (filters?: FilterParams): string => {
  const filterString = filters?.filter ? parameterizeArrayofObjects(filters.filter) : ''
  const paramString = filterString ? `(filter: ${filterString})` : ''

  return gql`
    {
      bucket${paramString} {
        edges {
          node {
            name
          }
        }
      }
    }
  `
}

export const mapQueryAwsBucketFilters = (raw: any): string[] => {
  try {
    const list = raw.bucket.edges.map(({ node }) => node.name || '')
    return [...new Set<string>(list)]
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryTemplates = (datasourceId?: string): string => {
  const paramsStr = datasourceId ? `(datasourceId:"${datasourceId}")` : ''
  return gql`{
  template${paramsStr} {
    edges {
      node {
        id
        templateName
      }
    }
  }
}
`
}

export const mapQueryTemplates = (raw: any): { list: { id: string; name: string }[] } => {
  const list = raw.template?.edges?.map(({ node }) => ({
    id: node.id,
    name: node.templateName
  }))
  return { list }
}

export const queryJiraProjectIdNames = (dataSourceId: string): string => {
  return gql`
    {
      ticketStoreProject(datasourceId: "${dataSourceId}") {
        edges {
          node {
            id
            projectName
          }
        }
      }
    }
  `
}

export const mapQueryJiraProjectIdNames = (raw: any): { list: { id: string; name: string }[] } => {
  try {
    return {
      list: raw.ticketStoreProject.edges.map(({ node: { projectName, id } }) => ({
        name: projectName,
        id
      }))
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryReportersNameEmail = (): string => {
  return gql`
    {
      ticket {
        edges {
          node {
            reporter {
              name
              email
            }
          }
        }
      }
    }
  `
}

export const mapQueryReportersNameEmail = (
  raw: any
): { list: { email: string; name: string }[] } => {
  try {
    const list = raw.ticket.edges.map(({ node: { reporter } }) => ({
      name: reporter.name,
      email: reporter.email
    }))

    const uniqueReporters = [...new Map(list.map((item) => [item.email, item])).values()] as {
      email: string
      name: string
    }[]
    return {
      list: uniqueReporters
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryAssigneesNameEmail = (): string => {
  return gql`
    {
      ticket {
        edges {
          node {
            assignee {
              name
              email
            }
          }
        }
      }
    }
  `
}

export const mapQueryAssigneesNameEmail = (
  raw: any
): { list: { email: string; name: string }[] } => {
  try {
    const list = raw.ticket.edges
      .filter(({ node: { assignee } }) => assignee?.email)
      .map(({ node: { assignee } }) => ({
        name: assignee.name,
        email: assignee.email
      }))

    const uniqueAssignees = [...new Map(list.map((item) => [item.email, item])).values()] as {
      email: string
      name: string
    }[]
    return {
      list: uniqueAssignees
    }
  } catch (error) {
    console.error(error)
    throw error
  }
}

export const queryExternalUsersFilter = (params: ExternalUsersFiltersParam): string => {
  const { alertId, datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT } = params
  const skip = calculateSkipAndLimit(page, pageSize)
  const id = alertId ? `, id: "${alertId}"` : ''
  const dataSourceId = datasourceId ? `datasourceIds: ${JSON.stringify([params.datasourceId])}` : ''
  const externalUserFilter = `(${skip ? `skip: ${skip}, ` : ''}limit: ${pageSize})`
  return gql`
  {
    alert(${dataSourceId} ${id}, first: 1) {
      edges {
        node {
          externalUserCount
          externalUser${externalUserFilter} {
            user
            objectCount
          }
        }
      }
    }
  }
  
`
}

export const mapQueryExternalUsersFilter = (
  raw: any
): { list: { user: string; objectsCount: number }[]; total: number } => {
  try {
    const externalUserCount = raw.alert?.edges[0]?.node?.externalUserCount
    const list = raw.alert?.edges[0]?.node?.externalUser?.map(({ user, objectCount }) => ({
      user,
      objectsCount: objectCount
    }))

    return { list, total: externalUserCount }
  } catch (e) {
    console.error(e)
    return { list: [], total: 0 }
  }
}

export const queryAllSendersPolicies = (params: EmailFiltersParam): string => {
  const { alertId, datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT } = params
  const cursor = getAfterCursor(page, pageSize)

  let mailFilterStr = `(first: ${pageSize}, after: "${cursor}"`

  if (datasourceId) {
    mailFilterStr += `, datasourceId: "${datasourceId}"`
  }

  if (alertId) {
    mailFilterStr += `, filter: [{key: ALERT_IDS, values: ${JSON.stringify(alertId)}}]`
  }

  mailFilterStr += ')'
  return gql`
  {
    mailGroupBySender${mailFilterStr} {
      count
      edges{
        node{
          sender{
            email
          }
        }
      }
    }
  }
    `
}

export const mapQueryAllSendersPolicies = (raw: any): { list: string[]; total: number } => {
  let list = []
  if (raw && raw.mailGroupBySender) {
    if (raw.mailGroupBySender.edges && Array.isArray(raw.mailGroupBySender.edges)) {
      list = raw.mailGroupBySender.edges.map(({ node: { sender } }) => sender.email)
    }
  }

  return { list, total: raw.mailGroupBySender?.count || 0 }
}

export const queryAllRecipientsPolicies = (params: EmailFiltersParam): string => {
  const { alertId, datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT } = params
  const cursor = getAfterCursor(page, pageSize)

  let mailFilterStr = `(first: ${pageSize}, after: "${cursor}"`

  if (datasourceId) {
    mailFilterStr += `, datasourceId: "${datasourceId}"`
  }

  if (alertId) {
    mailFilterStr += `, filter: [{key: ALERT_IDS, values: ${JSON.stringify(alertId)}}]`
  }

  mailFilterStr += ')'
  return gql`
  {
    mailGroupByUser${mailFilterStr} {
      count
      edges{
        node{
          mailbox{
            email
          }
        }
      }
    }
  }
    `
}

export const mapQueryAllRecipientsPolicies = (raw: any): { list: string[]; total: number } => {
  let list = []
  if (raw && raw.mailGroupByUser) {
    if (raw.mailGroupByUser.edges && Array.isArray(raw.mailGroupByUser.edges)) {
      list = raw.mailGroupByUser.edges.map(({ node: { mailbox } }) => mailbox.email)
    }
  }

  return { list, total: raw.mailGroupByUser?.count || 0 }
}

export type FetchSharePointSitesParams = IGetPaginatedFiltersParams & {
  datasourceId: string
}
export const querySharePointSites = (params: FetchSharePointSitesParams): string => {
  const { datasourceId, page = 1, pageSize = FILTERS_LIMIT_DEFAULT } = params
  const cursor = getAfterCursor(page, pageSize)

  return gql`
    {
      site(
        datasourceIds: ["${datasourceId}"]
        first: ${pageSize}
        after: "${cursor}"
      ) {
        count
        edges {
          node {
            id
            siteId
            displayName
          }
        }
      }
    }
  `
}

export type SharePointSite = {
  id: string
  siteId: string
  displayName: string
}

export const mapSharePointSites = (
  raw: any
): { sharePointSites: SharePointSite[]; total: number } => {
  const sharePointSites = raw.site.edges.map(({ node }) => ({
    id: node.id,
    siteId: node.siteId,
    displayName: node.displayName
  }))

  return { sharePointSites, total: raw.site.count }
}
